<template>
  <div>
    <v-dialog
      v-model="showClinicDialog"
      min-width="500"
      max-width="700"
      @keydown.esc="onCloseClinicDialog" @click:outside="onCloseClinicDialog"
    >
      <template>
        <droopy-clinic-maintainer
          :clinic="selectedClinic"
          @close-dialog="onCloseClinicDialog"
        ></droopy-clinic-maintainer>
      </template>
    </v-dialog>

    <v-dialog v-model="confirmDisable" max-width="420">
      <v-card>
        <v-card-title class="text-h6">
          {{ disableTarget && isClinicActive(disableTarget) ? 'Deshabilitar clínica' : 'Habilitar clínica' }}
        </v-card-title>
        <v-card-text>
          <template v-if="disableTarget && isClinicActive(disableTarget)">
            Se deshabilitará la cuenta de administrador de
            <strong>{{ disableTarget.clinicName }}</strong> y dejará de poder operar el panel.
          </template>
          <template v-else>
            Se habilitará nuevamente la cuenta de administrador de
            <strong>{{ disableTarget && disableTarget.clinicName }}</strong>.
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDisable = false">Cancelar</v-btn>
          <v-btn color="primary" text @click="confirmDisableAction">Confirmar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmResendTempPassword" max-width="420">
      <v-card>
        <v-card-title class="text-h6">Reenviar contraseña temporal</v-card-title>
        <v-card-text>
          Se generará una nueva contraseña temporal para el administrador de
          <strong>{{ resendTempPasswordTarget && resendTempPasswordTarget.clinicName }}</strong>
          y se le reenviará el correo de bienvenida {{ resendRecipient(resendTempPasswordTarget) }}.
          La contraseña temporal anterior dejará de funcionar.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmResendTempPassword = false">Cancelar</v-btn>
          <v-btn color="primary" text @click="confirmResendTempPasswordAction">Reenviar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de sospecha (019-mark-suspicious-entities-ui) -->
    <SuspiciousFlagModal
      v-model="showSuspiciousModal"
      :rut="suspiciousTarget && suspiciousTarget.clinicRut"
      entity-type="clinic"
      :entity-label="(suspiciousTarget && suspiciousTarget.clinicName) || 'esta clínica'"
      :can-flag="can('droopy-clinics:flag-suspicious')"
      @saved="onSuspiciousSaved"
    />

    <v-container fluid>
      <v-row class="ma-0 my-4 align-center justify-space-between">
        <h1 class="text-h5 font-weight-bold">Droopy</h1>
        <v-btn
          v-if="can('droopy-clinics:create')"
          color="primary"
          class="square-btn"
          @click="openCreateDialog"
        >
          <v-icon left>mdi-plus</v-icon>Agregar clínica
        </v-btn>
      </v-row>
      <v-card outlined>
        <v-data-table
          :headers="headers"
          :items="clinics"
          item-key="clinicId"
          :loading="loadingClinics"
          no-data-text="Todavía no hay clínicas Droopy configuradas."
          :footer-props="{
            'items-per-page-options': [10, 20, 30, 40, 50]
          }"
        >
          <template v-slot:item.status="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" :color="isClinicActive(item) ? 'success' : 'grey'">
                  {{ isClinicActive(item) ? 'mdi-check-circle' : 'mdi-close-circle' }}
                </v-icon>
              </template>
              <span>{{ isClinicActive(item) ? 'Activa' : 'Inactiva' }}</span>
            </v-tooltip>
          </template>
          <template v-slot:item.networkType="{ item }">
            {{ networkTypeLabel(item.networkType) }}
          </template>
          <template v-slot:item.visibleInApp="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" :color="item.visibleInApp ? 'success' : 'grey'">
                  {{ item.visibleInApp ? 'mdi-check-circle' : 'mdi-close-circle' }}
                </v-icon>
              </template>
              <span>{{ item.visibleInApp ? 'Se muestra en la app' : 'No se muestra en la app' }}</span>
            </v-tooltip>
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="d-flex align-center justify-end actions-cell">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    fab
                    depressed
                    outlined
                    x-small
                    v-bind="attrs"
                    v-on="on"
                    color="secondary"
                    :loading="togglingVisibilityId === item.clinicId"
                    :disabled="!!togglingVisibilityId"
                    @click="toggleVisibleInApp(item)"
                  >
                    <v-icon small>{{ item.visibleInApp ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                  </v-btn>
                </template>
                <span>{{ item.visibleInApp ? 'Dejar de mostrar en la app' : 'Mostrar en la app' }}</span>
              </v-tooltip>
              <user-actions
                :user="item"
                :show-actions="rowActions"
                @edit="openEditDialog"
                @suspicious="openSuspicious"
              ></user-actions>
              <v-tooltip v-if="can('droopy-clinics:reset-password')" top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    fab
                    depressed
                    outlined
                    x-small
                    v-bind="attrs"
                    v-on="on"
                    color="secondary"
                    :loading="resendingTempPasswordId === item.clinicId"
                    :disabled="resendingTempPasswordId === item.clinicId"
                    @click="askResendTempPassword(item)"
                  >
                    <v-icon small>mdi-lock-reset</v-icon>
                  </v-btn>
                </template>
                <span>Reenviar contraseña temporal</span>
              </v-tooltip>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    fab
                    dark
                    depressed
                    x-small
                    v-bind="attrs"
                    v-on="on"
                    color="primary"
                    :to="servicesRoute(item)"
                  >
                    <v-icon small dark>mdi-medical-bag</v-icon>
                  </v-btn>
                </template>
                <span>Ver servicios</span>
              </v-tooltip>
              <v-tooltip v-if="can('droopy-clinics:toggle-status')" top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    fab
                    dark
                    depressed
                    x-small
                    v-bind="attrs"
                    v-on="on"
                    :color="isClinicActive(item) ? 'error' : 'success'"
                    :loading="togglingId === item.clinicId"
                    :disabled="!!togglingId"
                    @click="askDisable(item)"
                  >
                    <v-icon small dark>{{ isClinicActive(item) ? 'mdi-account-cancel' : 'mdi-account-check' }}</v-icon>
                  </v-btn>
                </template>
                <span>{{ isClinicActive(item) ? 'Deshabilitar' : 'Habilitar' }}</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </v-container>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import ClinicsService from '../services/clinics.service';
import DroopyClinicMaintainer from '../components/DroopyClinicMaintainer.vue';
import UserActions from '../components/UserActions.vue';
import SuspiciousFlagModal from '../components/SuspiciousFlagModal.vue';
import { utilsMixin } from '../mixins/utilsMixin';
import { formatMissingFields, networkTypeLabel } from '../utils/red-pawer.utils';
import { can } from '../utils/permissions';

// Códigos de rechazo del contrato 085 de pawer-backend (reset-temporary-password.md). Lo que
// no esté acá (INVALID_CLINIC_ID, 500, errores de red) cae en el mensaje genérico.
const RESEND_TEMP_PASSWORD_MESSAGES = {
  CLINIC_ADMIN_ALREADY_ACTIVATED: {
    tone: 'warning',
    text: 'El administrador ya activó su cuenta; esta acción es solo para contraseñas temporales vencidas. Si olvidó su contraseña, puede recuperarla desde el login de Droopy con «¿Olvidaste tu contraseña?».'
  },
  CLINIC_ADMIN_NOT_FOUND: {
    tone: 'error',
    text: 'Esta clínica no tiene una cuenta de administrador.'
  },
  CLINIC_ADMIN_NOT_ACTIVE: {
    tone: 'error',
    text: 'La cuenta de administrador de esta clínica está inactiva. Habilítala antes de reenviar la contraseña.'
  },
  CLINIC_ADMIN_EMAIL_MISSING: {
    tone: 'error',
    text: 'La cuenta de administrador no tiene un email registrado, así que no hay dónde enviar la contraseña.'
  },
  CLINIC_ADMIN_ACCOUNT_MISSING: {
    tone: 'error',
    text: 'La cuenta de administrador no tiene un usuario de acceso asociado. Contacta al equipo técnico.'
  },
  CLINIC_ADMIN_ACCOUNT_DISABLED: {
    tone: 'error',
    text: 'El acceso del administrador de esta clínica está deshabilitado. Contacta al equipo técnico.'
  }
};

const RESEND_TEMP_PASSWORD_GENERIC_ERROR = 'Ocurrió un error al reenviar la contraseña temporal. Intenta nuevamente.';

export default {
  name: 'DroopyClinics',

  components: { DroopyClinicMaintainer, UserActions, SuspiciousFlagModal },

  mixins: [utilsMixin],

  data() {
    return {
      clinics: [],
      loadingClinics: false,
      headers: [
        { text: 'Estado', value: 'status', sortable: false },
        { text: 'Nombre', value: 'clinicName' },
        { text: 'Email', value: 'email' },
        { text: 'Tipo', value: 'networkType' },
        { text: 'En la app', value: 'visibleInApp', align: 'center' },
        { text: 'Acciones', value: 'actions', sortable: false, align: 'center' }
      ],
      showClinicDialog: false,
      selectedClinic: null,
      confirmDisable: false,
      disableTarget: null,
      togglingId: null,
      togglingVisibilityId: null,
      confirmResendTempPassword: false,
      resendTempPasswordTarget: null,
      resendingTempPasswordId: null,
      showSuspiciousModal: false,
      suspiciousTarget: null,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: ''
    };
  },

  computed: {
    /** Row actions of `user-actions`, limited to what the admin may do. */
    rowActions() {
      const actions = [];
      if (can('droopy-clinics:update') || can('droopy-clinics:update-bank-details')) {
        actions.push('edit');
      }
      if (can('droopy-clinics:flag-suspicious')) actions.push('suspicious');
      return actions;
    }
  },

  mounted() {
    this.fetchClinics();
  },

  methods: {
    can,
    async fetchClinics() {
      this.loadingClinics = true;
      const res = await ClinicsService.getDroopyClinics();
      if (res.status === 'ok') {
        this.clinics = res.data;
      } else {
        this.showErrorSnackbar('No se pudieron cargar las clínicas Droopy.');
      }
      this.loadingClinics = false;
    },

    openCreateDialog() {
      this.selectedClinic = null;
      this.showClinicDialog = true;
    },

    openEditDialog(clinic) {
      this.selectedClinic = clinic;
      this.showClinicDialog = true;
    },

    onCloseClinicDialog() {
      this.showClinicDialog = false;
      this.selectedClinic = null;
      this.fetchClinics();
    },

    isClinicActive(item) {
      return item.status === 'active';
    },

    // Es un enlace (no un push) para poder abrirlo en otra pestaña.
    servicesRoute(item) {
      return {
        path: `/clinics/droopy/${item.clinicId}/services`,
        query: { clinicName: item.clinicName }
      };
    },

    askDisable(item) {
      this.disableTarget = item;
      this.confirmDisable = true;
    },

    async confirmDisableAction() {
      const target = this.disableTarget;
      this.confirmDisable = false;
      if (!target) return;

      this.togglingId = target.clinicId;
      const nextStatus = this.isClinicActive(target) ? 'inactive' : 'active';
      const res = await ClinicsService.setDroopyClinicStatus(target.clinicId, nextStatus);
      if (res.status === 'ok') {
        target.status = (res.data && res.data.status) || nextStatus;
      } else {
        this.showErrorSnackbar('Ocurrió un error al cambiar el estado de la clínica. Intenta nuevamente.');
      }
      this.togglingId = null;
    },

    askResendTempPassword(item) {
      this.resendTempPasswordTarget = item;
      this.confirmResendTempPassword = true;
    },

    async confirmResendTempPasswordAction() {
      const target = this.resendTempPasswordTarget;
      this.confirmResendTempPassword = false;
      if (!target) return;

      this.resendingTempPasswordId = target.clinicId;
      const res = await ClinicsService.resendClinicAdminTempPassword(target.clinicId);
      this.notifyResendResult(res, target);
      this.resendingTempPasswordId = null;
    },

    notifyResendResult(res, target) {
      if (res.status === 'ok') {
        // La contraseña ya se regeneró aunque el correo falle; reintentar genera otra.
        if (res.data && res.data.emailSent === true) {
          this.showSuccessSnackbar(`Se envió una nueva contraseña temporal ${this.resendRecipient(target)}.`);
        } else {
          this.showWarningSnackbar('Se generó una nueva contraseña temporal, pero no se pudo enviar el correo. Intenta nuevamente.');
        }
        return;
      }
      const known = RESEND_TEMP_PASSWORD_MESSAGES[res.message];
      if (known && known.tone === 'warning') {
        this.showWarningSnackbar(known.text);
      } else {
        this.showErrorSnackbar(known ? known.text : RESEND_TEMP_PASSWORD_GENERIC_ERROR);
      }
    },

    resendRecipient(target) {
      return target && target.email ? `a ${target.email}` : 'al email de la cuenta';
    },

    networkTypeLabel(type) {
      return networkTypeLabel(type);
    },

    async toggleVisibleInApp(item) {
      const visibleInApp = !item.visibleInApp;
      this.togglingVisibilityId = item.clinicId;
      const res = await ClinicsService.setNetworkVisibility(item.clinicId, visibleInApp);
      if (res.status === 'ok') {
        item.visibleInApp = res.data && typeof res.data.visibleInApp === 'boolean'
          ? res.data.visibleInApp
          : visibleInApp;
        this.showSuccessSnackbar(
          item.visibleInApp ? 'La clínica ahora se muestra en la app.' : 'La clínica ya no se muestra en la app.'
        );
      } else {
        this.showErrorSnackbar(this.visibilityErrorMessage(res));
      }
      this.togglingVisibilityId = null;
    },

    visibilityErrorMessage(res) {
      if (res.httpCode === 422 && res.missing.length) {
        return `No se puede mostrar en la app. Falta: ${formatMissingFields(res.missing)}. Complétalo en Editar.`;
      }
      return 'Ocurrió un error al cambiar la visibilidad en la app. Intenta nuevamente.';
    },

    openSuspicious(item) {
      this.suspiciousTarget = item;
      this.showSuspiciousModal = true;
    },

    onSuspiciousSaved() {
      this.showSuccessSnackbar('Sospecha actualizada correctamente.');
    }
  }
};
</script>

<style scoped>
.square-btn {
  border-radius: 8px !important;
}

.actions-cell {
  gap: 8px;
  flex-wrap: nowrap;
}
/* The eye toggle and the Red Pawer columns narrow this cell; without this the
   edit/suspicious group wraps onto two rows. */
.actions-cell > * {
  flex-shrink: 0;
}
.actions-cell ::v-deep .actions-container {
  flex-wrap: nowrap;
}
</style>
